import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Briefcase, Plus, X, Pencil, Trash2, Tag } from "lucide-react";
import { toast } from "sonner";



export default function RolesAdmin({ user }) {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editRole, setEditRole] = useState(null);
  const [title, setTitle] = useState("");
  const [department, setDepartment] = useState("");
  const [competencies, setCompetencies] = useState([]);
  const [newComp, setNewComp] = useState("");

  const fetchRoles = async () => {
    try {
      const resp = await api.get("/roles");
      setRoles(resp.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  const openCreate = () => {
    setEditRole(null);
    setTitle("");
    setDepartment("");
    setCompetencies([]);
    setShowDialog(true);
  };

  const openEdit = (role) => {
    setEditRole(role);
    setTitle(role.title);
    setDepartment(role.department || "");
    setCompetencies(role.competencies || []);
    setShowDialog(true);
  };

  const addCompetency = () => {
    if (newComp.trim() && !competencies.includes(newComp.trim())) {
      setCompetencies([...competencies, newComp.trim()]);
      setNewComp("");
    }
  };

  const removeCompetency = (comp) => {
    setCompetencies(competencies.filter((c) => c !== comp));
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error("Title is required");
      return;
    }
    try {
      if (editRole) {
        await api.put(`/roles/${editRole.role_id}`, { title, department, competencies });
        toast.success("Role updated");
      } else {
        await api.post("/roles", { title, department, competencies });
        toast.success("Role created");
      }
      setShowDialog(false);
      fetchRoles();
    } catch (e) {
      toast.error("Failed to save role");
    }
  };

  const handleDelete = async (roleId) => {
    if (!window.confirm("Delete this role?")) return;
    try {
      await api.delete(`/roles/${roleId}`);
      toast.success("Role deleted");
      fetchRoles();
    } catch (e) {
      toast.error("Failed to delete role");
    }
  };

  return (
    <div className="px-4 py-6 sm:p-6 md:p-8 lg:p-12 space-y-4 lg:space-y-6 animate-fade-in" data-testid="roles-admin-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white uppercase">Roles & Competencies</h1>
          <p className="text-zinc-500 mt-1">Define roles and the competencies candidates are evaluated against</p>
        </div>
        <Button
          data-testid="create-role-btn"
          onClick={openCreate}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" /> New Role
        </Button>
      </div>

      {/* Roles Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : roles.length === 0 ? (
        <div className="text-center py-20">
          <Briefcase className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400 mb-4">No roles defined yet</p>
          <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700 text-white">
            <Plus className="w-4 h-4 mr-2" /> Create your first role
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4" data-testid="roles-grid">
          {roles.map((role) => (
            <Card key={role.role_id} className="bg-zinc-900/50 border-white/10 hover:border-white/20 transition-colors" data-testid={`role-card-${role.role_id}`}>
              <CardContent className="p-5 space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-base font-semibold text-white">{role.title}</h3>
                    {role.department && <p className="text-xs text-zinc-500 mt-0.5">{role.department}</p>}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      data-testid={`edit-role-${role.role_id}`}
                      variant="ghost"
                      size="sm"
                      onClick={() => openEdit(role)}
                      className="h-8 w-8 p-0 text-zinc-500 hover:text-blue-400"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      data-testid={`delete-role-${role.role_id}`}
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(role.role_id)}
                      className="h-8 w-8 p-0 text-zinc-500 hover:text-red-400"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>

                {role.competencies?.length > 0 ? (
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                      <Tag className="w-3 h-3" /> Competencies
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {role.competencies.map((c, i) => (
                        <span key={i} className="px-2.5 py-1 text-xs rounded-md bg-blue-500/10 text-blue-400 border border-blue-500/20">
                          {c}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-zinc-600">No competencies defined</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-md" data-testid="role-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold text-white uppercase">
              {editRole ? "Edit Role" : "New Role"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="text-xs text-zinc-500 uppercase tracking-wider mb-1.5 block">Title</label>
              <Input
                data-testid="role-title-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Frontend Engineer"
                className="bg-zinc-800 border-white/10 text-zinc-200"
              />
            </div>
            <div>
              <label className="text-xs text-zinc-500 uppercase tracking-wider mb-1.5 block">Department</label>
              <Input
                data-testid="role-dept-input"
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                placeholder="e.g. Engineering"
                className="bg-zinc-800 border-white/10 text-zinc-200"
              />
            </div>
            <div>
              <label className="text-xs text-zinc-500 uppercase tracking-wider mb-1.5 block">Competencies</label>
              <div className="flex gap-2 mb-2">
                <Input
                  data-testid="competency-input"
                  value={newComp}
                  onChange={(e) => setNewComp(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCompetency())}
                  placeholder="Type and press Enter"
                  className="bg-zinc-800 border-white/10 text-zinc-200"
                />
                <Button
                  data-testid="add-competency-btn"
                  variant="outline"
                  onClick={addCompetency}
                  className="border-white/10 text-zinc-400 hover:text-white shrink-0"
                >
                  Add
                </Button>
              </div>
              <div className="flex flex-wrap gap-1.5 min-h-[2rem]">
                {competencies.map((c, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md bg-blue-500/10 text-blue-400 border border-blue-500/20">
                    {c}
                    <button onClick={() => removeCompetency(c)} className="hover:text-red-400 transition-colors">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="border-white/10 text-zinc-400">
              Cancel
            </Button>
            <Button
              data-testid="save-role-btn"
              onClick={handleSave}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {editRole ? "Update" : "Create"} Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
